import { Component, computed, effect, inject, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { Gender } from '../../../api/app-types/gender.app';
import { PositionShare, User } from '../../../api/app-types/user.app';
import { POSITION_SHARE_SERVICE } from '../../../api/services/position-share.service';
import { USER_SERVICE } from '../../../api/services/user.service';
import { NOTIFICATION_SERVICE } from '../../../shared/notification/notification';
import { createErrorNotification, createSuccessNotification } from '../../../shared/notification/notification.factory';
import { AdminUserView } from './admin-user-view/admin-user-view';
import { PositionShareEdit } from './position-share-edit/position-share-edit';
import { PositionShareList } from './position-share-list/position-share-list';
import { LvrHeading } from '../../../core/components/heading/heading';
import { NavCarousel } from '../../../core/components/nav-carousel/nav-carousel';
import { hasPermission } from '../../../shared/permissions/permission.func';
import { SessionManagerService } from '../../../shared/storage/session-manager.service';
import { createPositionShareReadAccess } from '../../../shared/permissions/position-share-access.helper';

@Component({
	selector: 'lvr-admin-user',
	imports: [
		TranslocoPipe,
		MatButtonModule,
		MatIconModule,
		AdminUserView,
		PositionShareList,
		PositionShareEdit,
		LvrHeading,
		NavCarousel,
	],
	templateUrl: './admin-user.html',
	styleUrl: './admin-user.scss',
})
export class AdminUser {
	private readonly router = inject(Router);
	private readonly route = inject(ActivatedRoute);
	private readonly routeData = toSignal(this.route.data);
	private readonly positionShareService = inject(POSITION_SHARE_SERVICE);
	private readonly notificationService = inject(NOTIFICATION_SERVICE);
	private readonly userService = inject(USER_SERVICE);
	private readonly transloco = inject(TranslocoService);
	private readonly session = inject(SessionManagerService);
	private readonly currentUserObjectId = signal<string | undefined>(undefined);
	private readonly currentUser = signal<User | undefined>(undefined);

	private readonly routeUser = computed(() => this.routeData()?.['user'] as User);
	private readonly userOverride = signal<User | undefined>(undefined);
	public readonly viewUser = computed(() => this.userOverride() ?? this.routeUser());
	private readonly positionShareReadAccess = createPositionShareReadAccess({
		userPermissions: this.session.permissions,
		viewedUser: this.viewUser,
		currentUser: this.currentUser,
		currentUserObjectId: this.currentUserObjectId,
	});

	public readonly genders = computed(() => this.routeData()?.['genders'] ?? []);
	public readonly roles = computed(() => this.routeData()?.['roles'] ?? []);
	public readonly integrationOffices = computed(() => this.routeData()?.['integrationOffices'] ?? []);
	public readonly specializations = computed(() => this.routeData()?.['specializations'] ?? []);

	// We don’t have a single item GET yet, so we pass the full PositionShare via signal instead of routing.
	// We set the state in ?editShare=<id> so Back just closes the editor rather than leaving the page.
	public editingShare = signal<PositionShare | undefined>(undefined);
	public readonly canReadAllPositionShares = this.positionShareReadAccess.canReadAllPositionShares;
	public readonly canReadOwnPositionShares = this.positionShareReadAccess.canReadOwnPositionShares;
	public readonly isOwnUserDetail = this.positionShareReadAccess.isOwnUserDetail;
	public readonly canReadPositionShares = this.positionShareReadAccess.canReadPositionShares;
	public readonly canAccessUserManagement = hasPermission(this.session.permissions, ['Users.Create', 'Users.Edit']);
	public readonly canEditUsers = hasPermission(this.session.permissions, 'Users.Edit');
	public readonly canEditPositionShares = hasPermission(this.session.permissions, 'Users.PositionShares.Edit');

	public readonly userGender = computed(() => {
		const u = this.viewUser();
		if (!u) return '—';

		const id = String(u.genderId);
		const g = this.genders().find((gender: Gender) => String(gender.id) === id);
		return g?.salutation ?? '—';
	});

	// Read the query params as signals
	private readonly queryParams = toSignal(this.route.queryParamMap, {
		initialValue: this.route.snapshot.queryParamMap,
	});

	public constructor() {
		void this.loadCurrentUserObjectId();
		effect(() => {
			const id = this.queryParams().get('editShare');
			// So we can close the position share edit form with 'back'
			if (!id && this.editingShare()) this.editingShare.set(undefined);
		});
	}

	private async loadCurrentUserObjectId() {
		try {
			const me = await this.userService.getSingleMe();
			this.currentUserObjectId.set(me.objectId);
			this.currentUser.set(me);
		} catch {
			this.currentUserObjectId.set(undefined);
			this.currentUser.set(undefined);
		}
	}

	public readonly backLink = computed(() => {
		const tree = this.router.createUrlTree(['../'], { relativeTo: this.route });
		return this.router.serializeUrl(tree);
	});

	public readonly carouselElements = computed(() =>
		[this.viewUser()].map((u) => ({
			id: u.id.toString(),
			label: u.displayName,
		})),
	);

	public openEdit() {
		this.router.navigate(['edit'], { relativeTo: this.route });
	}

	/**
	 * Opens the Position Share editor and sets the state in ?editShare=<id>.
	 **/
	public onEditShare = (share: PositionShare) => {
		if (!this.canEditPositionShares()) return;
		this.editingShare.set(share);
		this.router.navigate([], {
			relativeTo: this.route,
			queryParams: { editShare: share.id },
			queryParamsHandling: 'merge',
			replaceUrl: false, // for closing it with 'back'
		});
	};

	/**
	 * Handles the position share changes save event.
	 * Updates the position share, the user panel, closes the editor, and clears ?editShare.
	 */
	public onShareSaved = async (share: PositionShare) => {
		if (!this.canEditPositionShares()) return;
		try {
			await this.positionShareService.update(this.editingShare()!.id, share);
			const updated = await this.userService.getSingle(this.viewUser()!.objectId);
			this.userOverride.set(updated);

			this.router.navigate([], {
				relativeTo: this.route,
				queryParams: { editShare: null },
				queryParamsHandling: 'merge',
				replaceUrl: false,
			});
			this.editingShare.set(undefined);
			this.notificationService.addNotification(
				createSuccessNotification(this.transloco.translate('base.notification_message.changes_saved')),
			);
		} catch {
			this.notificationService.addNotification(
				createErrorNotification(this.transloco.translate('base.notification_message.changes_save_error')),
			);
		}
	};

	/**
	 * Cancels the position share edit mode without saving changes.
	 */
	public onShareCancel = () => {
		this.editingShare.set(undefined);
		this.router.navigate([], {
			relativeTo: this.route,
			queryParams: { editShare: null },
			queryParamsHandling: 'merge',
			replaceUrl: false,
		});
	};
}
